import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteProject, fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { RiDeleteBinFill, RiEyeLine, RiPencilLine, RiCloseLine } from "react-icons/ri";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import ProjectFilter from "./ProjectFilter";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import { canDeleteProject } from "../../constants/projectRules";
import { mapProjectToTableRow } from "./projectMappers";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { PROJECTS_LIST_PAGE_SIZE } from "../../Redux/Thunks/projectManagementThunkUtils";

const ProjectList = ({
  projectsData,
  /** Full list for Project Name filter options (GET all-projects) */
  allProjectsData = null,
  clientsData = null,
  /** Optional; when omitted, delete guard treats as no loaded work items (see `canDeleteProject`). */
  issuesData = { list: [] },
  loading,
  searchTerm,
  currentPage,
  setCurrentPage,
  limit = PROJECTS_LIST_PAGE_SIZE,
  onPageSizeChange,
  listingFilter,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  onRefetchProjects,
  onProjectClick,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const menuRefs = useRef({});
  const { can } = usePermissions();

  const canViewProject = can(PERMISSION_KEYS.PROJECTS_VIEW);
  const canViewProjectDashboard = can(PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW);
  const canOpenProjectWorkspace = canViewProject || canViewProjectDashboard;
  const canEditProject = can(PERMISSION_KEYS.PROJECTS_EDIT);
  const canDeleteProjectPerm = can(PERMISSION_KEYS.PROJECTS_DELETE);
  /** Only keys that render a control in the Action column (not list-only `PROJECTS_VIEW`). */
  const showActionsColumn = canViewProjectDashboard || canEditProject || canDeleteProjectPerm;
  const colCount = showActionsColumn ? 5 : 4;

  // List from API (already filtered by clientName, projectName, status on server)
  const listFromApi = projectsData?.list ?? [];
  const meta = projectsData?.meta ?? null;

  // Optional client-side search on current page only
  const filteredProjects = !searchTerm
    ? listFromApi
    : listFromApi.filter(
        (project) =>
          project.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.projectKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const hasData = listFromApi.length > 0;

  // Sorting
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredProjects, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  // Reset to page 1 when sorting changes
  useEffect(() => {
    if (sortKey) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDirection]);

  // Pagination from API meta (list is already one page)
  const totalCount = meta?.total ?? meta?.totalCount ?? filteredProjects.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / (limit || PROJECTS_LIST_PAGE_SIZE)));
  const paginatedProjects = sortedData;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(menuRefs.current).forEach((id) => {
        if (menuRefs.current[id] && !menuRefs.current[id].contains(event.target)) {
          setOpenMenuId(null);
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = (projectId) => {
    setOpenMenuId(openMenuId === projectId ? null : projectId);
  };

  const handleDeleteClick = (project) => {
    if (!canDeleteProjectPerm) {
      setDeniedPermissionKey(PERMISSION_KEYS.PROJECTS_DELETE);
      return;
    }
    setOpenMenuId(null);
    setProjectToDelete(project);
  };

  const hasOpenWorkItems = projectToDelete
    ? !canDeleteProject(projectToDelete, issuesData?.list)
    : false;

  const handleConfirmDelete = async () => {
    if (!projectToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteProject(projectToDelete.id)).unwrap();
      dispatch(fetchProjectsData({ getAll: true, force: true }));
      if (onRefetchProjects) onRefetchProjects();
      else dispatch(fetchProjectsData({ page: 1, limit: limit || PROJECTS_LIST_PAGE_SIZE }));
      setProjectToDelete(null);
    } catch (err) {
      // Keep modal open on error
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="clients-page-content !bg-transparent">
      <div className="w-full ">
        <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
          {/* Filter Section - always show (API filter: clientName, projectName, status) */}
          <div className="mb-3">
            <ProjectFilter
              listingFilter={listingFilter}
              handleFilterChange={handleFilterChange}
              handleFilterReset={handleFilterReset}
              applyFilters={applyFilters}
              projectsData={projectsData}
              allProjectsData={allProjectsData}
              clientsData={clientsData}
            />
          </div>

          {/* Table Card */}
          <div>
            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md  shadow-sm transition-shadow mt-4">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse mb-0 clients-table ">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              className="content-cell"
                              columnKey="clientName"
                              label="Client"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="projectName"
                              label="Project Name"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              className="content-cell"
                              columnKey="receivedDate"
                              label="Start Date"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="status"
                              label="Status"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              sortable={false}
                            />
                            {showActionsColumn ? (
                              <SortableHeader
                                columnKey="action"
                                label="Action"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="action-col"
                              />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody className="relative">
                          {loading ? (
                            <tr aria-hidden="true">
                              <td colSpan={colCount} className="min-h-[240px] h-60 p-0 border-0" />
                            </tr>
                          ) : (
                            <>
                              {paginatedProjects.length > 0 ? (
                                paginatedProjects.map((project) => {
                                  const row = mapProjectToTableRow(project);
                                  return (
                                    <tr key={project.id}>
                                      <td className="text-start align-middle">
                                        <Typography variant="span1">{row.clientName}</Typography>
                                      </td>

                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          onClick={
                                            canOpenProjectWorkspace && onProjectClick
                                              ? () => onProjectClick(project)
                                              : undefined
                                          }
                                          className={
                                            canOpenProjectWorkspace && onProjectClick
                                              ? "cursor-pointer underline hover:opacity-80 focus:outline-none text-text-link"
                                              : ""
                                          }
                                        >
                                          {row.projectName}
                                        </Typography>
                                      </td>

                                      <td className="text-start align-middle">
                                        <Typography variant="span1">
                                          {row.startDateDisplay}
                                        </Typography>
                                      </td>

                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          className={`badge rounded-full px-3 py-1 text-white text-xs font-bold ${row.statusColorClass}`}
                                        >
                                          {row.status}
                                        </Typography>
                                      </td>

                                      {showActionsColumn ? (
                                        <td className="text-start align-middle">
                                          <div className="flex gap-0.5 items-center justify-end gap-2">
                                            {canViewProjectDashboard ? (
                                              <FloatingTooltip label="View Dashboard">
                                                <Button
                                                  type="button"
                                                  onClick={() =>
                                                    onProjectClick && onProjectClick(project)
                                                  }
                                                  aria-label="View Dashboard"
                                                  size="sm"
                                                  className="btn-info-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiEyeLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canEditProject ? (
                                              <FloatingTooltip label="Edit">
                                                <Button
                                                  onClick={() =>
                                                    navigate(`/projects/edit/${project.id}`, {
                                                      state: {
                                                        previousPath:
                                                          location.pathname +
                                                          (location?.search || ""),
                                                      },
                                                    })
                                                  }
                                                  aria-label="Edit project"
                                                  size="sm"
                                                  className="
                                        btn-warning-new
                                        !w-7 !h-7  
                                        !p-0 
                                        !font-normal 
                                        !leading-none
                                        flex items-center justify-center
                                        border-0
                                      "
                                                >
                                                  <RiPencilLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canDeleteProjectPerm ? (
                                              <FloatingTooltip label="Delete">
                                                <Button
                                                  onClick={() => handleDeleteClick(project)}
                                                  aria-label="Delete project"
                                                  size="sm"
                                                  className="btn-danger-new !w-7 !h-7  !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={colCount} className="text-center">
                                    <NoDataFound
                                      message={
                                        searchTerm
                                          ? "No projects found matching your search"
                                          : "No projects available"
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center mt-2 text-center sm:text-start"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {listFromApi.length > 0 && (
            <ListPaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </ContentLoader>
      </div>

      {/* Warning modal: project has open work items — cannot delete */}
      {projectToDelete && hasOpenWorkItems && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
          onClick={() => setProjectToDelete(null)}
        >
          <div
            className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <Typography variant="h5" className="font-semibold text-text-primary">
                Cannot delete project
              </Typography>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setProjectToDelete(null)}
                className="!border-0 !shadow-none !bg-transparent !p-0 !min-w-0"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5 text-text-secondary" />
              </Button>
            </div>
            <div className="p-4">
              <Typography variant="text2" className="text-text-primary">
                This project (<strong>{projectToDelete?.projectName}</strong>) has open work items.
                Close or resolve those work items before deleting the project.
              </Typography>
              <div className="flex justify-end mt-6">
                <Button variant="primary" size="md" onClick={() => setProjectToDelete(null)}>
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation: only when project has no open work items */}
      <DeleteConfirmModal
        isOpen={!!projectToDelete && !hasOpenWorkItems}
        onClose={() => setProjectToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete project"
        itemName={projectToDelete?.projectName}
        loading={deleteLoading}
      />
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

export default ProjectList;
